/**
 * activeWorkoutPersistence
 * ------------------------
 * Tiny helper around localStorage that lets the ActiveWorkout page survive
 * navigating away (to Social, Coach Hub, etc.) and then "Continue" exactly
 * where the user left off — same sets ticked, same weights typed, same
 * elapsed timer.
 *
 * Schema (single slot, only one in-progress workout at a time):
 *   key: "gymgrizz_active_workout"
 *   value: {
 *     workout_id: string,
 *     wk:        full workout document with mutated sets,
 *     startedAt: epoch ms,
 *     alarmSound:string,
 *     idleMs:    accumulated idle time (phone-locked / PWA-backgrounded),
 *                subtracted from the elapsed clock so a workout left open
 *                overnight doesn't report an 8h duration. Set + maintained
 *                by `accumulateIdleSinceHidden`.
 *     hiddenAt:  epoch ms set when the tab visibility went hidden so we
 *                can compute how long the user was away on next visible.
 *     savedAt:   epoch ms (TTL guard — 24h),
 *   }
 *
 * We also dispatch a same-tab `gymgrizz:active-changed` window event so the
 * floating "Continue workout" banner can re-render the moment state is
 * saved or cleared (the native `storage` event only fires cross-tab).
 */
const KEY = "gymgrizz_active_workout";
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

function _emitChanged() {
  try {
    window.dispatchEvent(new Event("gymgrizz:active-changed"));
  } catch {/* ignore */}
}

export function saveActive(state) {
  try {
    const payload = { ...state, savedAt: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(payload));
    _emitChanged();
  } catch {/* localStorage full / disabled */}
}

export function loadActive() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.workout_id) return null;
    if (typeof parsed.savedAt === "number" && Date.now() - parsed.savedAt > TTL_MS) {
      // Stale (>24h). Clear it so the user gets a fresh template next time.
      clearActive();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearActive() {
  try {
    localStorage.removeItem(KEY);
    _emitChanged();
  } catch {/* ignore */}
}

/**
 * markHidden / accumulateIdleSinceHidden
 * --------------------------------------
 * Called by the global visibilitychange listener (mounted in
 * ActiveWorkoutBanner, which lives in ProtectedLayout). On hidden we stamp
 * `hiddenAt`; on visible we compute the elapsed away-time and accumulate
 * it into `idleMs`. Result: the workout's elapsed clock and the final
 * `duration_seconds` exclude any time the phone was locked / PWA was
 * backgrounded — even across page changes.
 */
export function markHidden() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.workout_id) return;
    parsed.hiddenAt = Date.now();
    localStorage.setItem(KEY, JSON.stringify(parsed));
  } catch {/* ignore */}
}

export function accumulateIdleSinceHidden() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.workout_id || !parsed.hiddenAt) return;
    const idle = Date.now() - parsed.hiddenAt;
    if (idle > 0) {
      parsed.idleMs = (parsed.idleMs || 0) + idle;
    }
    delete parsed.hiddenAt;
    localStorage.setItem(KEY, JSON.stringify(parsed));
    _emitChanged();
  } catch {/* ignore */}
}
